import React from 'react';
import styled from 'styled-components';
import { Size } from '../../types';
import { PillsTooltip, QuestionOptions } from '../../atoms/pills-tooltip';
import { CarModel, CarState } from '../../atoms/car-model';
import { CargoModel, CargoState } from '../../atoms/cargo-model';
import { CarItem, CargoItem } from '../../enums';

type VehicleItem = CarItem | CargoItem;

interface VehicleState extends CarState, CargoState {}

interface VehicleItemState extends QuestionOptions {
  name: string;
  cssRule: string;
}

interface Props {
  className?: string;
  cargo?: boolean;
  size?: Size;
  onStateChange?: (vehicleState: VehicleState) => void;
}

interface State {
  showTooltip: boolean;
  vehicleItem: VehicleItem;
  vehicleState: VehicleState;
}

const VEHICLE_ITEM_STATES: VehicleItemState[] = [
  {
    name: 'minor',
    value: 'minor',
    cssRule: `filter: grayscale(100%) brightness(75%) sepia(100%)
    hue-rotate(-3deg) saturate(225%) contrast(0.95);`,
  },
  {
    name: 'moderate',
    value: 'moderate',
    cssRule: `filter: grayscale(100%) brightness(50%) sepia(100%)
    hue-rotate(-25deg) saturate(275%) contrast(0.95);`,
  },
  {
    name: 'severe',
    value: 'severe',
    cssRule: `filter: grayscale(100%) brightness(40%) sepia(100%)
    hue-rotate(-50deg) saturate(550%) contrast(0.8);`,
  },
];

class VehicleDamageModel extends React.Component<Props, State> {
  static defaultProps = {
    size: 'md' as Size,
  };

  state = {
    showTooltip: false,
    vehicleItem: '' as VehicleItem,
    vehicleState: {} as VehicleState,
  };

  toolTipCloseHandler = () => this.setState({ showTooltip: false });

  vehicleItemClickHandler = (vehicleItem: VehicleItem) => {
    this.setState({ showTooltip: true, vehicleItem });
  };

  onChange = results => {
    const { onStateChange } = this.props;
    const { vehicleItem, vehicleState } = this.state;
    const value = results.length ? results[0] : '';
    const updatedState = {
      ...vehicleState,
      [vehicleItem]: value,
    };

    if (value === vehicleState[vehicleItem]) {
      delete updatedState[vehicleItem];
    }

    this.setState({ vehicleState: updatedState });
    onStateChange && onStateChange(updatedState);
  };

  render() {
    const { className, cargo, size } = this.props;
    const { showTooltip, vehicleItem, vehicleState } = this.state;
    const popupTitle = vehicleItem.replace(/-/g, ' ');

    return (
      <div className={`${className} ${size}`}>
        {cargo ? (
          <CargoModel
            vehicleState={vehicleState}
            vehicleImgFilters={VEHICLE_ITEM_STATES}
            clickHandler={this.vehicleItemClickHandler}
          />
        ) : (
          <CarModel
            vehicleState={vehicleState}
            vehicleImgFilters={VEHICLE_ITEM_STATES}
            clickHandler={this.vehicleItemClickHandler}
          />
        )}
        <PillsTooltip
          show={showTooltip}
          onClose={this.toolTipCloseHandler}
          onChange={this.onChange}
          title={popupTitle}
          options={VEHICLE_ITEM_STATES}
          selected={vehicleState[vehicleItem]}
        />
      </div>
    );
  }
}

const StyledVehicleDamageModel = styled(VehicleDamageModel)`
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;

  &.xs {
    font-size: 0.45rem;
  }

  &.sm {
    font-size: 0.6rem;
  }

  &.md {
    font-size: 0.75rem;
  }

  &.lg {
    font-size: 0.9rem;
  }

  &.xl {
    font-size: 1rem;
  }
`;

export default StyledVehicleDamageModel;
